/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import { MedliSearchView } from '../componentViews/SearchView';
import { MedliStaticListView } from '../componentViews/StaticListView';

import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';
import { useMedliList } from '../../hooks/useMedli';

import RouteNames from '../../appAuthorized/RouteNames';
import { eMedliCategoryName } from '../../../constants/MedliNames';
import HealthEntryCategoryNames, { AllergySubType } from '../../../constants/HealthEntryCategoryNames';

interface GeneralProps {
  match: any;
}

const HealthEntryChangeStaticList: React.FC<GeneralProps> = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);

  const [, navActions] = useNavigate();
  const [{ category }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [medliList] = useMedliList(category);

  let title = '';
  let medliCategory = null;
  switch (category) {
    case HealthEntryCategoryNames.vaccination:
      title = 'vaccinationSearch.search.title.text';
      medliCategory = eMedliCategoryName.vaccination;
      break;
    default:
      break;
  }

  return (
    <MedliStaticListView
      suggestedResults={medliList.list}
      title={title}
      medliCategory={medliCategory}
      onGoBack={() => {
        navActions.goBack(RouteNames.healthEntry(healthEntryId));
      }}
      selectSearchResult={(medliTermData) => {
        const newHE = heActions.instantiateHealthEntry();
        newHE.medliTerm.replaceWithSearchTerm(medliTermData);

        heActions.apiUpdate(newHE.data())
          .then(() => {
            navActions.goBack(RouteNames.healthEntry(healthEntryId));
          });
      }}
    />
  );
};

export const HealthEntryChange: React.FC<GeneralProps> = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);
  const [{ category, subCategory }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [, navActions] = useNavigate();

  let medliCategory = null;
  let queryPlaceholder = '';
  let medliSearchCategory = null;

  switch (category) {
    case HealthEntryCategoryNames.allergy:
      if (subCategory === AllergySubType.food) {
        medliCategory = eMedliCategoryName.foodAllergy;
        medliSearchCategory = eMedliCategoryName.food;
        queryPlaceholder = 'allergyFoodSearch.search.field.hint';
      } else if (subCategory === AllergySubType.medication) {
        medliCategory = eMedliCategoryName.medicationAllergy;
        medliSearchCategory = eMedliCategoryName.medicationName;
        queryPlaceholder = 'allergyMedicationSearch.search.field.hint';
      } else {
        medliCategory = eMedliCategoryName.allergy;
        medliSearchCategory = eMedliCategoryName.allergy;
        queryPlaceholder = 'allergyOtherSearch.search.field.hint';
      }
      break;
    case HealthEntryCategoryNames.condition:
      queryPlaceholder = 'conditionSearch.search.field.hint';
      medliCategory = eMedliCategoryName.condition;
      medliSearchCategory = eMedliCategoryName.condition;
      break;
    case HealthEntryCategoryNames.procedure:
      queryPlaceholder = 'procedureSearch.search.field.hint';
      medliCategory = eMedliCategoryName.procedure;
      medliSearchCategory = eMedliCategoryName.procedure;
      break;
    case HealthEntryCategoryNames.medication:
      queryPlaceholder = 'medicationSearch.search.field.hint';
      medliCategory = eMedliCategoryName.medication;
      medliSearchCategory = eMedliCategoryName.medication;
      break;
    case HealthEntryCategoryNames.otherTreatment:
      queryPlaceholder = 'treatmentSearch.search.field.hint';
      medliCategory = eMedliCategoryName.otherTreatment;
      medliSearchCategory = eMedliCategoryName.otherTreatment;
      break;
    case HealthEntryCategoryNames.vaccination:
      return <HealthEntryChangeStaticList {...props} />;
    case HealthEntryCategoryNames.lab:
      queryPlaceholder = 'labSearch.search.field.hint';
      medliCategory = eMedliCategoryName.lab;
      medliSearchCategory = eMedliCategoryName.lab;
      break;
    case HealthEntryCategoryNames.measurement:
      queryPlaceholder = 'measurementSearch.search.field.hint';
      medliCategory = eMedliCategoryName.measurement;
      medliSearchCategory = eMedliCategoryName.measurement;
      break;
    case HealthEntryCategoryNames.geneticResult:
      queryPlaceholder = 'geneSearch.search.field.hint';
      medliCategory = eMedliCategoryName.gene;
      medliSearchCategory = eMedliCategoryName.gene;
      break;
    default:
      break;
  }

  if (!medliCategory) { return null; }

  return (
    <MedliSearchView
      medliCategory={medliCategory}
      medliSearchCategory={medliSearchCategory}
      hasSuggestions={false}
      queryPlaceholder={queryPlaceholder}
      suggestedResults={[]}
      onSelectResult={(medliSearchResult) => {
        return new Promise(() => {
          const newHE = heActions.instantiateEditingHealthEntry();
          newHE.medliTerm.replaceWithSearchTerm(medliSearchResult.searchResult.data);
          heActions.apiUpdate(newHE.data()).then(() => {
            navActions.goBack(RouteNames.healthEntry(healthEntryId));
          });
        });
      }}
      onGoBack={() => {
        navActions.goBack(RouteNames.healthEntry(healthEntryId));
      }}
    />
  );
};
